import { useSupplier } from "@/context/SupplierContext";
import { Building2, ChevronsUpDown, Layers } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function BuyerSwitcher() {
  const { buyers, activeBuyer, selected, setSelected, isAll } = useSupplier();
  const totalActive = buyers.reduce((a, b) => a + b.active_pos, 0);

  if (!buyers.length) {
    return (
      <span data-testid="buyer-switcher" className="flex items-center gap-2.5 rounded-lg border border-dashed bg-white px-3 py-1.5 text-left">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-200 text-slate-500"><Building2 className="h-3.5 w-3.5" /></span>
        <span>
          <span className="block text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Viewing Buyer</span>
          <span className="block text-sm font-semibold leading-tight text-slate-400">Select Buyer</span>
        </span>
      </span>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="buyer-switcher" className="flex items-center gap-2.5 rounded-lg border bg-white px-3 py-1.5 text-left transition-all duration-150 hover:-translate-y-[1px] hover:shadow-sm">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-900 text-white">
            {isAll ? <Layers className="h-3.5 w-3.5" /> : <Building2 className="h-3.5 w-3.5" />}
          </span>
          <span>
            <span className="block text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Viewing Buyer</span>
            <span className="block max-w-44 truncate text-sm font-semibold leading-tight" data-testid="buyer-switcher-label">
              {isAll ? "All Buyer Organizations" : activeBuyer?.name || "…"}
            </span>
          </span>
          <ChevronsUpDown className="h-4 w-4 text-slate-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 bg-white" data-testid="buyer-switcher-menu">
        <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">Buyer Organizations</DropdownMenuLabel>
        {buyers.map((b) => (
          <DropdownMenuItem
            key={b.org_id}
            data-testid={`switch-buyer-${b.name.replace(/\W+/g, "-").toLowerCase()}`}
            onClick={() => setSelected(b.org_id)}
            className={`flex cursor-pointer items-center gap-2.5 px-3 py-2.5 ${selected === b.org_id ? "bg-slate-100" : ""}`}
          >
            <Building2 className="h-4 w-4 shrink-0 text-slate-500" />
            <span className="flex-1">
              <span className="block text-sm font-medium">{b.name}</span>
              <span className="block text-xs text-muted-foreground">{b.active_pos} Active POs{b.on_time_pct != null ? ` · ${b.on_time_pct}% On-Time` : ""}</span>
            </span>
            {selected === b.org_id && <span className="h-2 w-2 rounded-full bg-slate-900" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="switch-buyer-all"
          onClick={() => setSelected("all")}
          className={`flex cursor-pointer items-center gap-2.5 px-3 py-2.5 ${isAll ? "bg-slate-100" : ""}`}
        >
          <Layers className="h-4 w-4 shrink-0 text-slate-500" />
          <span className="flex-1">
            <span className="block text-sm font-medium">All Buyer Organizations</span>
            <span className="block text-xs text-muted-foreground">{totalActive} Active POs across {buyers.length} buyers</span>
          </span>
          {isAll && <span className="h-2 w-2 rounded-full bg-slate-900" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
